'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { defaultRowHeight } from './constants';
import { IVirtualListProps, IVirtualRange } from './interfaces';

const emptyRange: IVirtualRange = { startIndex: 0, stopIndex: -1 };

const useVirtualList = ({
  bodyHeight,
  overscanCount,
  backwardOverscan = true,
  data = [],
}: IVirtualListProps) => {
  const sizeMap = useRef<{ [key: number]: number }>({});
  // Bumped whenever a measured row size changes, so `getSize` gets a new identity and
  // react-window rebuilds its row bounds cache (replacement for v1 `resetAfterIndex`).
  const [sizesVersion, setSizesVersion] = useState(0);
  const [range, setRange] = useState<IVirtualRange>(emptyRange);

  const setSize = useCallback((index: number, size: number): void => {
    if (sizeMap.current[index] === size) {
      return;
    }

    sizeMap.current[index] = size;
    setSizesVersion((version) => version + 1);
  }, []);

  const getSize = useCallback(
    (index: number): number => sizeMap.current[index] || defaultRowHeight,
    [sizesVersion],
  );

  const offsets = useMemo((): number[] => {
    const result = new Array<number>(data.length + 1);
    result[0] = 0;

    for (let index = 0; index < data.length; index++) {
      result[index + 1] = result[index] + getSize(index);
    }

    return result;
  }, [getSize, data.length]);

  const getOffset = useCallback(
    (index: number): number => offsets[Math.max(0, Math.min(index, data.length))] ?? 0,
    [offsets, data.length],
  );

  const totalHeight = offsets[data.length] ?? 0;

  const overscan = overscanCount || 2 * Math.round(
    (bodyHeight || (typeof window === 'undefined' ? 0 : window.innerHeight)) / defaultRowHeight,
  );

  const handleRowsRendered = useCallback((_visibleRows: IVirtualRange, allRows: IVirtualRange): void => {
    setRange((prevRange) => (
      prevRange.startIndex === allRows.startIndex && prevRange.stopIndex === allRows.stopIndex
        ? prevRange
        : allRows
    ));
  }, []);

  return {
    setSize,
    getSize,
    getOffset,
    totalHeight,
    overscan,
    backwardOverscan,
    range,
    handleRowsRendered,
  };
};

type VirtualListContextType = ReturnType<typeof useVirtualList>;

export const VirtualListContext = createContext<VirtualListContextType>(undefined);

export const useVirtualListContext = () => {
  const context = useContext(VirtualListContext);
  if (!context) {
    throw new Error('useVirtualListContext must be used within a VirtualListContextProvider');
  }
  return context;
};

interface IProps extends IVirtualListProps {
  children: React.ReactNode,
}

const VirtualListContextProvider = ({ children, ...props }: IProps) => (
  <VirtualListContext.Provider value={ useVirtualList(props) }>
    { children }
  </VirtualListContext.Provider>
);

export default VirtualListContextProvider;
